// src/pages/RegistroNoleggiPage.js
// Registro dei noleggi esterni (mezzi d'opera e veicoli) — a differenza di Parco Mezzi
// (che traccia i mezzi DI PROPRIETÀ dell'azienda), qui si registrano i noleggi da fornitori
// terzi: gru/piattaforme sono sempre legate a una commessa (il costo entra in automatico
// nel Controllo Economico, vedi ControlloEconomico.js), furgoni/auto sono generici aziendali.
// Il costo NON si inserisce mai a mano: si calcola da solo da data inizio/fine e tariffa
// (vedi utils/noleggiUtils.js), stesso principio già usato per le Presenze.
// W439: il noleggiatore si sceglie dall'Elenco Fornitori (components/SceltaNoleggiatore.js),
// il costo comprende i trasporti di consegna e ritiro, e «Per fornitore» è il resoconto
// economico di ogni noleggiatore (components/ResocontoNoleggiFornitori.js).
import React, { useState, useEffect, useMemo, useId } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, getDocs, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { useCanSee } from "../hooks/useCanSee";
import { logModifica } from "../utils/logModifica";
import { formatValutaItaliana } from "../utils/clienteHelpers";
import { Plus, Trash2, X, Lock, Star } from "lucide-react";
import { costoNoleggio, costoNoloNoleggio, trasportiNoleggio, erroreTrasporti, importoTrasportoValido, isNoleggioAttivo, giorniTrascorsiNoleggio, giorniFatturabiliNoleggio, giorniFatturabiliDiNoleggio, semestreNoleggio, calcolaCostoPrevisto, toLocalDateStr, TIPI_MEZZO_OPERA, TIPI_VEICOLO, scadenzaNoleggio, etichettaNoleggio, testoScadenzaNoleggio, eRettifica, testoRettifica } from "../utils/noleggiUtils";
import { numIt } from "../utils/economiaCommessa";
import SceltaNoleggiatore from "../components/SceltaNoleggiatore";
import ResocontoNoleggiFornitori from "../components/ResocontoNoleggiFornitori";

const FORM_VUOTO = {
  categoria: "mezzo_opera", tipo: "gru", identificativo: "", fornitore: "", fornitoreId: "",
  trasportoConsegna: "", trasportoRitiro: "", trasportoCompreso: false,
  projectId: "", tariffaTipo: "giornaliera", importoGiornaliero: "", importoForfettario: "",
  dataInizio: toLocalDateStr(new Date()), dataFinePrevista: "",
  escludiWeekend: false, escludiFestivi: false,
};

function oggi() { return toLocalDateStr(new Date()); }

// Anteprima costo nel form — stessa logica di costoNoleggio/giorniTrascorsiNoleggio ma
// applicata ai valori ancora in bozza (prima del salvataggio), usando la data fine prevista
// se c'è, altrimenti oggi come stima aperta. Puramente indicativa: il costo reale definitivo
// resta sempre quello calcolato da Controllo Economico sul documento salvato.
function anteprimaCosto(f) {
  const trasporti = trasportiNoleggio(f).totale;
  if (f.tariffaTipo === "forfettario") {
    const imp = numIt(f.importoForfettario);
    return imp > 0 ? { costo: imp + trasporti, nolo: imp, trasporti, giorni: null, aperta: false } : null;
  }
  const tariffa = numIt(f.importoGiornaliero);
  if (!(tariffa > 0) || !f.dataInizio) return null;
  const fine = f.dataFinePrevista || null;
  const giorni = giorniFatturabiliNoleggio(f.dataInizio, fine, { escludiWeekend: f.escludiWeekend, escludiFestivi: f.escludiFestivi });
  return { costo: giorni * tariffa + trasporti, nolo: giorni * tariffa, trasporti, giorni, aperta: !fine };
}

const inpBase = { width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box" };

// Trasporti del noleggiatore (W439): consegna in cantiere (obbligatoria, anche 0) e ritiro
// (proposto ora, confermato alla chiusura), oppure «compreso nella tariffa».
function CampiTrasporto({ f, set, bloccato = false, nota = "" }) {
  const id = useId();
  return (
    <div role="group" aria-label="Trasporto" style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "8px 10px", marginBottom: 10 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#9a3412", marginBottom: 6 }}>🚚 Trasporto del noleggiatore</div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", cursor: bloccato ? "default" : "pointer", marginBottom: 6 }}>
        <input type="checkbox" disabled={bloccato} checked={!!f.trasportoCompreso} onChange={e => set({ trasportoCompreso: e.target.checked })} />
        Trasporto compreso nella tariffa
      </label>
      {!f.trasportoCompreso && (
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor={`${id}-c`} style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Consegna in cantiere € *</label>
            <input id={`${id}-c`} disabled={bloccato} value={f.trasportoConsegna ?? ""} onChange={e => set({ trasportoConsegna: e.target.value })} placeholder="es. 250 (0 se gratis)" inputMode="decimal" style={inpBase} />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor={`${id}-r`} style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Ritiro a fine noleggio €</label>
            <input id={`${id}-r`} disabled={bloccato} value={f.trasportoRitiro ?? ""} onChange={e => set({ trasportoRitiro: e.target.value })} placeholder="es. 250" inputMode="decimal" style={inpBase} />
          </div>
        </div>
      )}
      <div style={{ fontSize: 10.5, color: "#9a3412", marginTop: 4 }}>{nota || "Entrano nel costo del noleggio e quindi nel Controllo Economico della commessa. Il ritiro si conferma quando chiudi il noleggio."}</div>
    </div>
  );
}

const numeroOVuoto = (v) => (String(v ?? "").trim() === "" ? null : Math.max(0, numIt(v) || 0));
const campiTrasporto = (f) => ({
  trasportoCompreso: !!f.trasportoCompreso,
  trasportoConsegna: f.trasportoCompreso ? null : numeroOVuoto(f.trasportoConsegna),
  trasportoRitiro: f.trasportoCompreso ? null : numeroOVuoto(f.trasportoRitiro),
});
const testoTrasporti = (n) => {
  if (n.trasportoCompreso) return "trasporto compreso";
  const t = trasportiNoleggio(n);
  if (n.trasportoConsegna == null && n.trasportoRitiro == null) return "";
  return `trasporti € ${formatValutaItaliana(t.totale)} (consegna € ${formatValutaItaliana(t.consegna)} · ritiro ${n.trasportoRitiro == null ? "da confermare" : "€ " + formatValutaItaliana(t.ritiro)})`;
};

export default function RegistroNoleggiPage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const vedeRettifiche = isOrgAdmin || isPlatformOwner; // «Visto» sulle rettifiche: solo titolare/admin
  const { effectiveOrgId } = useOrg();
  const canSee = useCanSee();
  const { addToast } = useToast();
  const canManage = isOrgAdmin || isPlatformOwner || canSee("registro-noleggi");
  const canChiudere = isOrgAdmin || isPlatformOwner || canSee("registro-noleggi-chiudi");

  const [noleggi, setNoleggi] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(FORM_VUOTO);
  const [saving, setSaving] = useState(false);
  const [chiudiTarget, setChiudiTarget] = useState(null);
  const [dataChiusuraForm, setDataChiusuraForm] = useState(oggi());
  const [savingChiusura, setSavingChiusura] = useState(false);
  const [correzione, setCorrezione] = useState(false); // true = correggere la fine di un noleggio già chiuso
  const [motivoRettifica, setMotivoRettifica] = useState("");
  const [riferimentoRettifica, setRiferimentoRettifica] = useState("");
  const [spostaTarget, setSpostaTarget] = useState(null);
  const [scheda, setScheda] = useState("elenco"); // elenco | fornitori | confronto
  const [modificaTarget, setModificaTarget] = useState(null);
  const [modificaForm, setModificaForm] = useState(null);
  const [savingModifica, setSavingModifica] = useState(false);
  const [sospensioniTarget, setSospensioniTarget] = useState(null);
  const [nuovaSospensione, setNuovaSospensione] = useState({ dal: "", al: "", motivo: "" });
  const [savingSospensione, setSavingSospensione] = useState(false);
  const [spostaForm, setSpostaForm] = useState({ nuovoProjectId: "", dataPassaggio: oggi() });
  const [fornitori, setFornitori] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [ritiroChiusura, setRitiroChiusura] = useState("");
  const puoAggiungereFornitori = isOrgAdmin || isPlatformOwner; // come l'Elenco Fornitori
  const [savingSposta, setSavingSposta] = useState(false);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db, "noleggi"), where("orgId", "==", effectiveOrgId)),
      snap => setNoleggi(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId)),
      snap => setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db, "fornitori"), where("organizationId", "==", effectiveOrgId)),
      snap => setFornitori(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    getDoc(doc(db, "organizations", effectiveOrgId)).then(s => { if (s.exists()) setOrgInfo(s.data()); }).catch(() => {});
  }, [effectiveOrgId]);

  const noleggiOrdinati = useMemo(() =>
    [...noleggi].sort((a, b) => {
      const aAttivo = isNoleggioAttivo(a), bAttivo = isNoleggioAttivo(b);
      if (aAttivo !== bAttivo) return aAttivo ? -1 : 1;
      return (b.dataInizio || "").localeCompare(a.dataInizio || "");
    }), [noleggi]);

  const daDecidere = useMemo(() => noleggi.map(n => ({ n, s: scadenzaNoleggio(n) })).filter(x => x.s)
    .sort((a, b) => (b.s.stato === "domani" ? -1 : b.s.giorni) - (a.s.stato === "domani" ? -1 : a.s.giorni)), [noleggi]);

  const projectsAperti = useMemo(() => projects.filter(p => p.statoCommessa !== "chiusa" && !p.isParent), [projects]);
  const TIPI = form.categoria === "mezzo_opera" ? TIPI_MEZZO_OPERA : TIPI_VEICOLO;

  // Scelto il noleggiatore: ripropone le scelte dell'ultimo noleggio con lui — weekend/festivi e,
  // per lo stesso tipo di mezzo, i costi di trasporto (se non sono già stati scritti).
  const scegliNoleggiatore = ({ id, denominazione }) => {
    setForm(f => {
      const prec = noleggi.filter(n => n.fornitoreId === id && id).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      const ultimo = prec[0];
      const stessoTipo = prec.find(n => n.tipo === f.tipo);
      const vuoto = String(f.trasportoConsegna).trim() === "" && String(f.trasportoRitiro).trim() === "" && !f.trasportoCompreso;
      return {
        ...f, fornitoreId: id, fornitore: denominazione,
        ...(ultimo ? { escludiWeekend: !!ultimo.escludiWeekend, escludiFestivi: !!ultimo.escludiFestivi } : {}),
        ...(stessoTipo && vuoto ? { trasportoCompreso: !!stessoTipo.trasportoCompreso, trasportoConsegna: stessoTipo.trasportoConsegna ?? "", trasportoRitiro: stessoTipo.trasportoRitiro ?? "" } : {}),
      };
    });
  };

  const salva = async () => {
    if (!form.fornitoreId || !fornitori.some(f => f.id === form.fornitoreId)) { addToast("Scegli il noleggiatore dall'elenco", "error"); return; }
    if (form.categoria === "mezzo_opera" && !form.projectId) { addToast("La commessa è obbligatoria per gru/piattaforme/mezzi d'opera", "error"); return; }
    // Senza una fine prevista nessuno viene avvisato quando il mezzo non serve più, e il costo
    // cresce in silenzio: per gru e piattaforme è obbligatoria (anche una stima, si sposta).
    if (form.categoria === "mezzo_opera" && !form.dataFinePrevista) { addToast("Indica la data di fine prevista: anche una stima, potrai prorogarla", "error"); return; }
    if (form.dataFinePrevista && form.dataInizio && form.dataFinePrevista < form.dataInizio) { addToast("La fine prevista non può essere prima dell'inizio", "error"); return; }
    if (form.tariffaTipo === "giornaliera" && !(numIt(form.importoGiornaliero) > 0)) { addToast("Inserisci la tariffa giornaliera", "error"); return; }
    if (form.tariffaTipo === "forfettario" && !(numIt(form.importoForfettario) > 0)) { addToast("Inserisci l'importo forfettario", "error"); return; }
    const errT = erroreTrasporti(form);
    if (errT) { addToast(errT, "error"); return; }
    const progetto = projects.find(p => p.id === form.projectId);
    setSaving(true);
    try {
      const payload = {
        orgId: effectiveOrgId, categoria: form.categoria, tipo: form.tipo,
        identificativo: form.identificativo.trim(), fornitore: form.fornitore.trim(), fornitoreId: form.fornitoreId,
        ...campiTrasporto(form),
        projectId: form.categoria === "mezzo_opera" ? form.projectId : (form.projectId || null),
        projectNome: progetto?.name || null,
        tariffaTipo: form.tariffaTipo,
        importoGiornaliero: form.tariffaTipo === "giornaliera" ? numIt(form.importoGiornaliero) || 0 : null,
        importoForfettario: form.tariffaTipo === "forfettario" ? numIt(form.importoForfettario) || 0 : null,
        dataInizio: form.dataInizio, dataFinePrevista: form.dataFinePrevista || null,
        dataFineEffettiva: null,
        escludiWeekend: !!form.escludiWeekend, escludiFestivi: !!form.escludiFestivi,
        costoPrevisto: calcolaCostoPrevisto(form.dataInizio, form.dataFinePrevista, form.tariffaTipo, form.importoGiornaliero, form.escludiWeekend, form.escludiFestivi),
        createdBy: currentUser?.uid, createdByNome: currentUser?.displayName || "",
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, "noleggi"), payload);
      logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "noleggi", refId: ref.id, refNome: `${TIPI[form.tipo]?.label} · ${form.fornitore}`,
        campo: "creazione", campoLabel: "Noleggio registrato", before: "", after: "Attivo",
        projectId: payload.projectId || "", projectName: payload.projectNome || "" });
      addToast("Noleggio registrato ✅", "success");
      setForm(FORM_VUOTO); setShowForm(false);
    } catch (err) {
      console.error("Errore registrazione noleggio:", err);
      addToast("Errore nel salvare: " + (err?.message || "errore sconosciuto"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Modifica — stessi campi della creazione tranne categoria/commessa (per cambiare
  // commessa c'è "Sposta", che gestisce correttamente il costo) e dataFineEffettiva
  // (per quella c'è "Chiudi"). Il caso più comune è allungare la data fine prevista
  // quando un noleggio dura più del previsto.
  const apriModifica = (n) => {
    setModificaTarget(n);
    setModificaForm({
      tipo: n.tipo, identificativo: n.identificativo || "", fornitore: n.fornitore, fornitoreId: n.fornitoreId || "",
      trasportoCompreso: !!n.trasportoCompreso, trasportoConsegna: n.trasportoConsegna ?? "", trasportoRitiro: n.trasportoRitiro ?? "",
      tariffaTipo: n.tariffaTipo, importoGiornaliero: n.importoGiornaliero ?? "", importoForfettario: n.importoForfettario ?? "",
      dataInizio: n.dataInizio, dataFinePrevista: n.dataFinePrevista || "",
      escludiWeekend: !!n.escludiWeekend, escludiFestivi: !!n.escludiFestivi,
    });
  };

  const salvaModifica = async () => {
    if (!modificaTarget || !modificaForm) return;
    if (!modificaForm.fornitoreId || !fornitori.some(f => f.id === modificaForm.fornitoreId)) { addToast("Scegli il noleggiatore dall'elenco", "error"); return; }
    const errT = erroreTrasporti(modificaForm);
    if (errT) { addToast(errT, "error"); return; }
    // a noleggio chiuso i trasporti sono un costo già registrato: li cambia solo chi può chiudere
    const trasportiBloccati = !!modificaTarget.dataFineEffettiva && !canChiudere;
    if (modificaForm.tariffaTipo === "giornaliera" && !(numIt(modificaForm.importoGiornaliero) > 0)) { addToast("Inserisci la tariffa giornaliera", "error"); return; }
    if (modificaForm.tariffaTipo === "forfettario" && !(numIt(modificaForm.importoForfettario) > 0)) { addToast("Inserisci l'importo forfettario", "error"); return; }
    if (modificaTarget.categoria === "mezzo_opera" && !modificaTarget.dataFineEffettiva && !modificaForm.dataFinePrevista) { addToast("Indica la data di fine prevista: anche una stima, potrai prorogarla", "error"); return; }
    if (modificaForm.dataFinePrevista && modificaForm.dataInizio && modificaForm.dataFinePrevista < modificaForm.dataInizio) { addToast("La fine prevista non può essere prima dell'inizio", "error"); return; }
    if (modificaTarget.dataFineEffettiva && modificaForm.dataInizio > modificaTarget.dataFineEffettiva) { addToast("La data di inizio non può essere successiva alla data di fine già registrata", "error"); return; }
    setSavingModifica(true);
    try {
      await updateDoc(doc(db, "noleggi", modificaTarget.id), {
        identificativo: modificaForm.identificativo.trim(), fornitore: modificaForm.fornitore.trim(), fornitoreId: modificaForm.fornitoreId,
        ...(trasportiBloccati ? {} : campiTrasporto(modificaForm)),
        tariffaTipo: modificaForm.tariffaTipo,
        importoGiornaliero: modificaForm.tariffaTipo === "giornaliera" ? numIt(modificaForm.importoGiornaliero) || 0 : null,
        importoForfettario: modificaForm.tariffaTipo === "forfettario" ? numIt(modificaForm.importoForfettario) || 0 : null,
        dataInizio: modificaForm.dataInizio, dataFinePrevista: modificaForm.dataFinePrevista || null,
        escludiWeekend: !!modificaForm.escludiWeekend, escludiFestivi: !!modificaForm.escludiFestivi,
        updatedAt: serverTimestamp(),
      });
      logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "noleggi", refId: modificaTarget.id, refNome: `${TIPI_MEZZO_OPERA[modificaForm.tipo]?.label || TIPI_VEICOLO[modificaForm.tipo]?.label} · ${modificaForm.fornitore}`,
        campo: "modifica", campoLabel: (modificaForm.dataFinePrevista || "") > (modificaTarget.dataFinePrevista || "") && modificaTarget.dataFinePrevista ? "Noleggio prorogato" : "Noleggio modificato", before: modificaTarget.dataFinePrevista || "", after: modificaForm.dataFinePrevista || "",
        projectId: modificaTarget.projectId || "", projectName: modificaTarget.projectNome || "" });
      addToast("Noleggio aggiornato ✅", "success");
      setModificaTarget(null); setModificaForm(null);
    } catch (err) {
      console.error("Errore modifica noleggio:", err);
      addToast("Errore nel salvare: " + (err?.message || "errore sconosciuto"), "error");
    } finally {
      setSavingModifica(false);
    }
  };

  // Sospensioni — periodi concordati col fornitore durante i quali il mezzo resta in
  // cantiere ma il costo non matura (es. ferie estive/natalizie). Solo titolare/amministrazione,
  // perché rappresentano un accordo commerciale reale col fornitore, non una semplice modifica.
  const aggiungiSospensione = async () => {
    if (!sospensioniTarget) return;
    if (!nuovaSospensione.dal || !nuovaSospensione.al) { addToast("Inserisci data inizio e fine della sospensione", "error"); return; }
    if (nuovaSospensione.al < nuovaSospensione.dal) { addToast("La data di fine sospensione non può precedere quella di inizio", "error"); return; }
    setSavingSospensione(true);
    try {
      const elenco = [...(sospensioniTarget.sospensioni || []), { dal: nuovaSospensione.dal, al: nuovaSospensione.al, motivo: nuovaSospensione.motivo.trim() }];
      await updateDoc(doc(db, "noleggi", sospensioniTarget.id), { sospensioni: elenco, updatedAt: serverTimestamp() });
      logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "noleggi", refId: sospensioniTarget.id, refNome: `${TIPI_MEZZO_OPERA[sospensioniTarget.tipo]?.label || TIPI_VEICOLO[sospensioniTarget.tipo]?.label} · ${sospensioniTarget.fornitore}`,
        campo: "sospensione", campoLabel: "Sospensione aggiunta", before: "", after: `${nuovaSospensione.dal} → ${nuovaSospensione.al}`,
        projectId: sospensioniTarget.projectId || "", projectName: sospensioniTarget.projectNome || "" });
      addToast("Sospensione aggiunta ✅", "success");
      setSospensioniTarget(t => ({ ...t, sospensioni: elenco }));
      setNuovaSospensione({ dal: "", al: "", motivo: "" });
    } catch (err) {
      console.error("Errore aggiunta sospensione:", err);
      addToast("Errore nel salvare: " + (err?.message || "errore sconosciuto"), "error");
    } finally {
      setSavingSospensione(false);
    }
  };

  const eliminaSospensione = async (index) => {
    if (!sospensioniTarget) return;
    const elenco = (sospensioniTarget.sospensioni || []).filter((_, i) => i !== index);
    try {
      await updateDoc(doc(db, "noleggi", sospensioniTarget.id), { sospensioni: elenco, updatedAt: serverTimestamp() });
      logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "noleggi", refId: sospensioniTarget.id, refNome: `${TIPI_MEZZO_OPERA[sospensioniTarget.tipo]?.label || TIPI_VEICOLO[sospensioniTarget.tipo]?.label} · ${sospensioniTarget.fornitore}`,
        campo: "sospensione", campoLabel: "Sospensione eliminata", before: "Presente", after: "Eliminata",
        projectId: sospensioniTarget.projectId || "", projectName: sospensioniTarget.projectNome || "" });
      setSospensioniTarget(t => ({ ...t, sospensioni: elenco }));
      addToast("Sospensione eliminata", "success");
    } catch (err) {
      console.error("Errore eliminazione sospensione:", err);
      addToast("Errore: " + (err?.message || "errore sconosciuto"), "error");
    }
  };

  const apriChiusura = (n, correggi = false) => {
    setChiudiTarget(n); setCorrezione(correggi);
    setDataChiusuraForm(correggi ? (n.dataFineEffettiva || oggi()) : oggi());
    setMotivoRettifica(""); setRiferimentoRettifica("");
    setRitiroChiusura(n.trasportoRitiro ?? "");
  };

  // Avviso in Nexiora a titolari e admin (tranne chi ha fatto la rettifica): un secondo paio
  // d'occhi su ogni costo già maturato che viene cambiato.
  const avvisaRettifica = async (n, r) => {
    try {
      const snap = await getDocs(query(collection(db, "users"), where("organizationId", "==", effectiveOrgId)));
      const destinatari = snap.docs.filter(d => ["titolare", "admin"].includes(d.data().role) && d.data().enabled !== false && d.id !== currentUser?.uid);
      const codice = projects.find(p => p.id === n.projectId)?.codiceCommessa;
      await Promise.all(destinatari.map(d => addDoc(collection(db, "notifiche"), {
        userId: d.id, orgId: effectiveOrgId, tipo: "noleggio_rettifica",
        titolo: "Rettifica di un noleggio",
        testo: `${etichettaNoleggio(n)}${n.projectNome ? ` su ${codice ? `[${codice}] ` : ""}${n.projectNome}` : ""}: ${testoRettifica(r)}`,
        projectId: n.projectId || null, noleggioId: n.id,
        letta: false, createdAt: serverTimestamp(),
      })));
    } catch (err) { console.warn("Avviso rettifica non inviato:", err?.code || err); }
  };

  const confermaChiusura = async () => {
    if (!chiudiTarget) return;
    if (!dataChiusuraForm) { addToast("Seleziona la data di fine noleggio", "error"); return; }
    if (dataChiusuraForm < chiudiTarget.dataInizio) { addToast("La data di fine non può essere precedente alla data di inizio", "error"); return; }
    if (dataChiusuraForm > oggi()) { addToast("La data di fine non può essere nel futuro", "error"); return; }
    const rettifica = eRettifica(dataChiusuraForm, correzione);
    if (correzione && dataChiusuraForm === chiudiTarget.dataFineEffettiva) { addToast("La data è uguale a quella già registrata", "info"); return; }
    if (rettifica && !motivoRettifica.trim()) { addToast("Scrivi il motivo della rettifica: resta registrato e lo vedono titolare e admin", "error"); return; }
    const chiedeRitiro = !correzione && !chiudiTarget.trasportoCompreso;
    if (chiedeRitiro && !importoTrasportoValido(ritiroChiusura)) { addToast("Indica il costo del trasporto di ritiro (0 se non c'è), es. 250 o 250,00", "error"); return; }
    setSavingChiusura(true);
    try {
      const etichetta = `${TIPI_MEZZO_OPERA[chiudiTarget.tipo]?.label || TIPI_VEICOLO[chiudiTarget.tipo]?.label} · ${chiudiTarget.fornitore}`;
      const agg = { dataFineEffettiva: dataChiusuraForm, updatedAt: serverTimestamp() };
      if (chiedeRitiro) agg.trasportoRitiro = Math.max(0, numIt(ritiroChiusura) || 0);
      let rec = null;
      if (rettifica) {
        rec = {
          il: new Date().toISOString(), uid: currentUser?.uid || "", nome: currentUser?.displayName || "",
          dataPrima: chiudiTarget.dataFineEffettiva || null, dataDopo: dataChiusuraForm,
          costoPrima: Math.round(costoNoleggio(chiudiTarget) * 100) / 100,
          costoDopo: Math.round(costoNoleggio({ ...chiudiTarget, dataFineEffettiva: dataChiusuraForm }) * 100) / 100,
          motivo: motivoRettifica.trim(), riferimento: riferimentoRettifica.trim(), vista: false,
        };
        agg.rettifiche = [...(chiudiTarget.rettifiche || []), rec];
        agg.rettificaDaVedere = true;
      }
      await updateDoc(doc(db, "noleggi", chiudiTarget.id), agg);
      logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "noleggi", refId: chiudiTarget.id, refNome: etichetta,
        campo: "dataFineEffettiva", campoLabel: rettifica ? "Rettifica noleggio" : "Chiusura noleggio",
        before: chiudiTarget.dataFineEffettiva || "Attivo", after: rettifica ? `${dataChiusuraForm} — ${motivoRettifica.trim()}` : dataChiusuraForm,
        projectId: chiudiTarget.projectId || "", projectName: chiudiTarget.projectNome || "" });
      if (rec) await avvisaRettifica(chiudiTarget, rec);
      addToast(rettifica ? "Rettifica registrata: avvisati titolare e admin ✅" : "Noleggio chiuso ✅", "success");
      setChiudiTarget(null);
    } catch (err) {
      console.error("Errore chiusura noleggio:", err);
      addToast("Errore nella chiusura: " + (err?.message || "errore sconosciuto"), "error");
    } finally {
      setSavingChiusura(false);
    }
  };

  const segnaRettificheViste = async (n) => {
    try {
      await updateDoc(doc(db, "noleggi", n.id), {
        rettifiche: (n.rettifiche || []).map(r => r.vista ? r : { ...r, vista: true, vistaDa: currentUser?.displayName || "", vistaIl: new Date().toISOString() }),
        rettificaDaVedere: false, updatedAt: serverTimestamp(),
      });
      addToast("Rettifica segnata come vista", "success");
    } catch (err) { addToast("Errore: " + (err?.message || "errore sconosciuto"), "error"); }
  };

  // Sposta un mezzo d'opera su un'altra commessa: chiude quello attuale il giorno prima
  // del passaggio (costo bloccato lì) e ne apre uno identico sulla nuova commessa dal
  // giorno del passaggio — nessun giorno contato due volte, nessuna commessa a doppio costo.
  // I due record restano collegati per tracciabilità (spostatoInId / spostatoDaId).
  const confermaSposta = async () => {
    if (!spostaTarget) return;
    if (spostaTarget.tariffaTipo === "forfettario") { addToast("Lo spostamento automatico non è disponibile per i noleggi a importo forfettario", "error"); return; }
    if (!spostaForm.nuovoProjectId) { addToast("Seleziona la commessa di destinazione", "error"); return; }
    if (spostaForm.nuovoProjectId === spostaTarget.projectId) { addToast("La commessa di destinazione è la stessa di partenza", "error"); return; }
    if (!spostaForm.dataPassaggio || spostaForm.dataPassaggio < spostaTarget.dataInizio) { addToast("La data di passaggio non può essere precedente all'inizio del noleggio", "error"); return; }
    const nuovoProgetto = projects.find(p => p.id === spostaForm.nuovoProjectId);
    const dataFineVecchio = new Date(spostaForm.dataPassaggio + "T00:00:00");
    dataFineVecchio.setDate(dataFineVecchio.getDate() - 1);
    const dataFineVecchioStr = toLocalDateStr(dataFineVecchio);
    if (dataFineVecchioStr < spostaTarget.dataInizio) { addToast("Con questa data di passaggio il noleggio di origine chiuderebbe prima ancora di iniziare — scegli una data successiva", "error"); return; }
    setSavingSposta(true);
    try {
      const nuovoRef = await addDoc(collection(db, "noleggi"), {
        orgId: effectiveOrgId, categoria: "mezzo_opera", tipo: spostaTarget.tipo,
        identificativo: spostaTarget.identificativo || "", fornitore: spostaTarget.fornitore, fornitoreId: spostaTarget.fornitoreId || "",
        // il mezzo non torna dal noleggiatore: nessuna nuova consegna; il ritiro lo paga la
        // commessa da cui il mezzo torna davvero, cioè questa nuova
        trasportoCompreso: !!spostaTarget.trasportoCompreso, trasportoConsegna: spostaTarget.trasportoCompreso ? null : 0,
        trasportoRitiro: spostaTarget.trasportoCompreso ? null : (spostaTarget.trasportoRitiro ?? null),
        projectId: spostaForm.nuovoProjectId, projectNome: nuovoProgetto?.name || null,
        tariffaTipo: spostaTarget.tariffaTipo,
        importoGiornaliero: spostaTarget.importoGiornaliero ?? null,
        importoForfettario: spostaTarget.importoForfettario ?? null,
        dataInizio: spostaForm.dataPassaggio, dataFinePrevista: spostaTarget.dataFinePrevista || null,
        dataFineEffettiva: null,
        escludiWeekend: !!spostaTarget.escludiWeekend, escludiFestivi: !!spostaTarget.escludiFestivi,
        costoPrevisto: calcolaCostoPrevisto(spostaForm.dataPassaggio, spostaTarget.dataFinePrevista, spostaTarget.tariffaTipo, spostaTarget.importoGiornaliero, spostaTarget.escludiWeekend, spostaTarget.escludiFestivi),
        spostatoDaId: spostaTarget.id,
        createdBy: currentUser?.uid, createdByNome: currentUser?.displayName || "",
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "noleggi", spostaTarget.id), { dataFineEffettiva: dataFineVecchioStr, spostatoInId: nuovoRef.id, ...(spostaTarget.trasportoCompreso ? {} : { trasportoRitiro: 0 }), updatedAt: serverTimestamp() });
      logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "noleggi", refId: spostaTarget.id, refNome: `${TIPI_MEZZO_OPERA[spostaTarget.tipo]?.label} · ${spostaTarget.fornitore}`,
        campo: "spostamento", campoLabel: "Noleggio spostato su altra commessa", before: spostaTarget.projectNome || "", after: nuovoProgetto?.name || "",
        projectId: spostaTarget.projectId || "", projectName: spostaTarget.projectNome || "" });
      addToast("Noleggio spostato ✅", "success");
      setSpostaTarget(null);
    } catch (err) {
      console.error("Errore spostamento noleggio:", err);
      addToast("Errore nello spostamento: " + (err?.message || "errore sconosciuto"), "error");
    } finally {
      setSavingSposta(false);
    }
  };

  const elimina = async (n) => {
    const tLabel = TIPI_MEZZO_OPERA[n.tipo]?.label || TIPI_VEICOLO[n.tipo]?.label || n.tipo;
    if (!(await confirm(`Eliminare il noleggio "${tLabel} · ${n.fornitore}"? Il relativo costo sparirà dal Controllo Economico.`, { type: "danger" }))) return;
    await deleteDoc(doc(db, "noleggi", n.id));
    logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
      sezione: "noleggi", refId: n.id, refNome: `${tLabel} · ${n.fornitore}`,
      campo: "eliminazione", campoLabel: "Noleggio eliminato", before: "Presente", after: "Eliminato",
      projectId: n.projectId || "", projectName: n.projectNome || "" });
    addToast("Eliminato", "success");
  };

  // Voto affidabilità — solo su noleggi già chiusi, solo titolare/amministrazione
  // (stesso permesso di Chiudi/Sposta). Un voto per noleggio, non per fornitore in
  // astratto: nel tempo la media dei voti per fornitore emerge da sola nella scheda
  // "Per fornitore", senza bisogno di un giudizio unico da mantenere aggiornato a mano.
  const vota = async (n, stelle) => {
    if (!canChiudere) return;
    try {
      await updateDoc(doc(db, "noleggi", n.id), { votoAffidabilita: stelle, votoData: oggi(), votoDa: currentUser?.uid || "", votoDaNome: currentUser?.displayName || "", updatedAt: serverTimestamp() });
      logModifica({ orgId: effectiveOrgId, uid: currentUser?.uid || "", nomeUtente: currentUser?.displayName || "",
        sezione: "noleggi", refId: n.id, refNome: `${TIPI_MEZZO_OPERA[n.tipo]?.label || TIPI_VEICOLO[n.tipo]?.label} · ${n.fornitore}`,
        campo: "votoAffidabilita", campoLabel: "Voto affidabilità fornitore", before: n.votoAffidabilita ? String(n.votoAffidabilita) : "", after: String(stelle),
        projectId: n.projectId || "", projectName: n.projectNome || "" });
      addToast("Voto registrato ✅", "success");
    } catch (err) {
      console.error("Errore voto noleggio:", err);
      addToast("Errore nel salvare il voto: " + (err?.message || "errore sconosciuto"), "error");
    }
  };

  // Confronto prezzi semestrale — solo mezzi d'opera a tariffa giornaliera: il forfettario
  // non ha un prezzo/giorno reale da confrontare, includerlo falserebbe il paragone.
  const confrontoPrezzi = useMemo(() => {
    const mappa = {};
    noleggi.filter(n => n.categoria === "mezzo_opera" && n.tariffaTipo === "giornaliera" && n.importoGiornaliero > 0).forEach(n => {
      const key = `${n.tipo}__${semestreNoleggio(n.dataInizio)}`;
      if (!mappa[key]) mappa[key] = { tipo: n.tipo, semestre: semestreNoleggio(n.dataInizio), fornitori: {} };
      const f = mappa[key].fornitori;
      if (!f[n.fornitore]) f[n.fornitore] = [];
      f[n.fornitore].push(numIt(n.importoGiornaliero));
    });
    return Object.values(mappa)
      .map(g => ({ ...g, fornitori: Object.entries(g.fornitori).map(([fornitore, prezzi]) => ({ fornitore, prezzoMedio: prezzi.reduce((s,p)=>s+p,0)/prezzi.length, count: prezzi.length })).sort((a,b) => a.prezzoMedio - b.prezzoMedio) }))
      .sort((a,b) => b.semestre.localeCompare(a.semestre) || a.tipo.localeCompare(b.tipo));
  }, [noleggi]);

  if (!canManage) return <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Non hai i permessi per gestire questa sezione.</div>;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#1e1b4b", marginBottom: 2 }}>🏗️ Registro e gestione noleggi</h2>
          <p style={{ fontSize: 12, color: "#6b7280" }}>Noleggi da fornitori esterni — gru e piattaforme sempre legate a una commessa (il costo entra da solo nel Controllo Economico), furgoni e auto generici aziendali.</p>
        </div>
        {scheda === "elenco" && (
          <button onClick={() => setShowForm(true)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
            <Plus size={14} /> Nuovo noleggio
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 18, borderBottom: "1px solid #e5e7eb" }}>
        {[{ id: "elenco", label: "Elenco" }, { id: "fornitori", label: "Per fornitore" }, { id: "confronto", label: "Confronto prezzi" }].map(s => (
          <button key={s.id} onClick={() => setScheda(s.id)}
            style={{ padding: "8px 14px", border: "none", borderBottom: `2px solid ${scheda === s.id ? "#7c3aed" : "transparent"}`, background: "none", color: scheda === s.id ? "#7c3aed" : "#6b7280", fontWeight: scheda === s.id ? 700 : 500, fontSize: 12.5, cursor: "pointer" }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Noleggi da decidere: fine prevista domani/oggi/passata, o aperti da molto senza data.
          Il costo non si ferma da solo: qualcuno deve chiudere il noleggio o prorogarlo. */}
      {scheda === "elenco" && daDecidere.length > 0 && (
        <div style={{ border: "1px solid #fecaca", background: "#fef2f2", borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#991b1b", marginBottom: 2 }}>⏰ Noleggi da chiudere o prorogare ({daDecidere.length})</div>
          <div style={{ fontSize: 11.5, color: "#7f1d1d", marginBottom: 8 }}>Se il mezzo non serve più, chiama il fornitore per il ritiro e chiudi il noleggio; se serve ancora, sposta in avanti la fine prevista.</div>
          {daDecidere.map(({ n, s: sc }) => (
            <div key={n.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: "1px solid #fee2e2", borderRadius: 9, padding: "8px 10px", marginBottom: 6 }}>
              <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#111827" }}>{etichettaNoleggio(n)}{n.projectNome ? <span style={{ fontWeight: 500, color: "#6b7280" }}> — {n.projectNome}</span> : null}</div>
                <div style={{ fontSize: 11.5, color: sc.stato === "domani" || sc.stato === "sospeso" ? "#92400e" : "#b91c1c", marginTop: 1 }}>{sc.stato === "sospeso" ? "⏸ " : ""}{testoScadenzaNoleggio(sc)}</div>
              </div>
              {canManage && (
                <button onClick={() => apriModifica(n)}
                  style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#6d28d9", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  Proroga
                </button>
              )}
              {canChiudere ? (
                <button onClick={() => apriChiusura(n)}
                  style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  Chiudi noleggio
                </button>
              ) : (
                <span style={{ fontSize: 10.5, color: "#9ca3af" }}>La chiusura la fa titolare o amministrazione</span>
              )}
            </div>
          ))}
        </div>
      )}

      {scheda === "elenco" && showForm && (
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>Nuovo noleggio</span>
            <button onClick={() => { setShowForm(false); setForm(FORM_VUOTO); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af" }}><X size={16} /></button>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {[{ id: "mezzo_opera", label: "🏗️ Mezzo d'opera" }, { id: "veicolo", label: "🚐 Veicolo" }].map(c => (
              <button key={c.id} type="button"
                onClick={() => setForm(f => ({ ...f, categoria: c.id, tipo: c.id === "mezzo_opera" ? "gru" : "furgone", projectId: c.id === "mezzo_opera" ? f.projectId : "" }))}
                style={{ flex: 1, padding: "8px", borderRadius: 8, border: `1px solid ${form.categoria === c.id ? "#7c3aed" : "#e5e7eb"}`, background: form.categoria === c.id ? "#f5f3ff" : "#fff", color: form.categoria === c.id ? "#7c3aed" : "#6b7280", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {c.label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {Object.entries(TIPI).map(([k, t]) => (
              <button key={k} type="button" onClick={() => setForm(f => ({ ...f, tipo: k }))}
                style={{ flex: 1, padding: "8px", borderRadius: 8, border: `1px solid ${form.tipo === k ? "#7c3aed" : "#e5e7eb"}`, background: form.tipo === k ? "#f5f3ff" : "#fff", color: form.tipo === k ? "#7c3aed" : "#6b7280", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {t.emoji} {t.label}
              </button>
            ))}
          </div>

          {form.categoria === "mezzo_opera" ? (
            <select value={form.projectId} onChange={e => setForm(f => ({ ...f, projectId: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 8, boxSizing: "border-box" }}>
              <option value="">— Seleziona commessa * —</option>
              {projectsAperti.map(p => <option key={p.id} value={p.id}>{p.codiceCommessa ? `[${p.codiceCommessa}] ` : ""}{p.name}</option>)}
            </select>
          ) : (
            <select value={form.projectId} onChange={e => setForm(f => ({ ...f, projectId: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 8, boxSizing: "border-box" }}>
              <option value="">Generico aziendale (nessuna commessa)</option>
              {projectsAperti.map(p => <option key={p.id} value={p.id}>{p.codiceCommessa ? `[${p.codiceCommessa}] ` : ""}{p.name}</option>)}
            </select>
          )}

          <SceltaNoleggiatore valore={form.fornitoreId} onScegli={scegliNoleggiatore} fornitori={fornitori} puoAggiungere={puoAggiungereFornitori} orgId={effectiveOrgId} style={{ marginBottom: 8 }} />
          <input value={form.identificativo} onChange={e => setForm(f => ({ ...f, identificativo: e.target.value }))} placeholder="Identificativo/targa (facoltativo)"
            style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 8, boxSizing: "border-box" }} />

          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Data inizio *</label>
              <input type="date" value={form.dataInizio} onChange={e => setForm(f => ({ ...f, dataInizio: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Data fine prevista{form.categoria === "mezzo_opera" ? " *" : ""}</label>
              <input type="date" value={form.dataFinePrevista} title="Anche una stima: il giorno prima ricevi un promemoria per chiuderlo o prorogarlo" onChange={e => setForm(f => ({ ...f, dataFinePrevista: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box" }} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", cursor: "pointer" }}>
              <input type="checkbox" checked={form.escludiWeekend} onChange={e => setForm(f => ({ ...f, escludiWeekend: e.target.checked }))} />
              Escludi sabato e domenica
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", cursor: "pointer" }}>
              <input type="checkbox" checked={form.escludiFestivi} onChange={e => setForm(f => ({ ...f, escludiFestivi: e.target.checked }))} />
              Escludi festivi italiani
            </label>
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {[{ id: "giornaliera", label: "Tariffa giornaliera" }, { id: "forfettario", label: "Importo forfettario" }].map(t => (
              <button key={t.id} type="button" onClick={() => setForm(f => ({ ...f, tariffaTipo: t.id }))}
                style={{ flex: 1, padding: "8px", borderRadius: 8, border: `1px solid ${form.tariffaTipo === t.id ? "#7c3aed" : "#e5e7eb"}`, background: form.tariffaTipo === t.id ? "#f5f3ff" : "#fff", color: form.tariffaTipo === t.id ? "#7c3aed" : "#6b7280", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                {t.label}
              </button>
            ))}
          </div>
          {form.tariffaTipo === "giornaliera" ? (
            <input value={form.importoGiornaliero} onChange={e => setForm(f => ({ ...f, importoGiornaliero: e.target.value }))} placeholder="€ / giorno *" inputMode="decimal"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 10, boxSizing: "border-box" }} />
          ) : (
            <input value={form.importoForfettario} onChange={e => setForm(f => ({ ...f, importoForfettario: e.target.value }))} placeholder="Importo totale € *" inputMode="decimal"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 10, boxSizing: "border-box" }} />
          )}

          <CampiTrasporto f={form} set={x => setForm(f => ({ ...f, ...x }))} />

          {(() => { const ant = anteprimaCosto(form); return ant && (
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "8px 14px", marginBottom: 12, fontSize: 12, color: "#5b21b6" }}>
              💰 {ant.giorni != null
                ? <>Anteprima: {ant.giorni} giorni × € {formatValutaItaliana(numIt(form.importoGiornaliero))}/giorno{ant.trasporti ? <> + trasporti € {formatValutaItaliana(ant.trasporti)}</> : null} = <strong>€ {formatValutaItaliana(ant.costo)}</strong>{ant.aperta ? " (fino ad oggi — imposta anche la data fine prevista per una stima fino a fine noleggio)" : " (fino alla data fine prevista)"}</>
                : <>Costo forfettario{ant.trasporti ? <> € {formatValutaItaliana(ant.nolo)} + trasporti € {formatValutaItaliana(ant.trasporti)}</> : null}: <strong>€ {formatValutaItaliana(ant.costo)}</strong></>}
            </div>
          ); })()}

          <button onClick={salva} disabled={saving} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: saving ? "#c4b5fd" : "#7c3aed", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer" }}>
            {saving ? "Salvo..." : "Salva"}
          </button>
        </div>
      )}

      {scheda === "elenco" && (noleggiOrdinati.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 12.5 }}>Nessun noleggio registrato ancora.</div>
      ) : noleggiOrdinati.map(n => {
        const t = TIPI_MEZZO_OPERA[n.tipo] || TIPI_VEICOLO[n.tipo] || { label: n.tipo, emoji: "📦" };
        const attivo = isNoleggioAttivo(n);
        const giorniCalendario = giorniTrascorsiNoleggio(n.dataInizio, n.dataFineEffettiva);
        const giorniFatturati = giorniFatturabiliDiNoleggio(n);
        const costo = costoNoleggio(n);
        const codice = projects.find(p => p.id === n.projectId)?.codiceCommessa;
        const residuo = n.costoPrevisto != null ? n.costoPrevisto - costoNoloNoleggio(n) : null; // il previsto è sulla sola tariffa
        const trasp = testoTrasporti(n);
        const haEsclusioni = n.escludiWeekend || n.escludiFestivi || (n.sospensioni?.length > 0);
        return (
          <div key={n.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#fff", border: `1px solid ${attivo ? "#e5e7eb" : "#f3f4f6"}`, borderRadius: 10, marginBottom: 6, opacity: attivo ? 1 : 0.8 }}>
            <span style={{ fontSize: 18 }}>{t.emoji}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                {t.label} {n.identificativo ? <span style={{ color: "#9ca3af", fontWeight: 500 }}>· {n.identificativo}</span> : ""}
                {attivo ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#059669", background: "#d1fae5", padding: "1px 6px", borderRadius: 5 }}>ATTIVO</span>
                  : <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#6b7280", background: "#f3f4f6", padding: "1px 6px", borderRadius: 5 }}>CONCLUSO</span>}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {n.fornitore} · {n.projectNome ? <>{codice ? `[${codice}] ` : ""}{n.projectNome}</> : "Generico aziendale"} · dal {new Date(n.dataInizio + "T00:00:00").toLocaleDateString("it-IT")}
                {n.dataFineEffettiva ? ` al ${new Date(n.dataFineEffettiva + "T00:00:00").toLocaleDateString("it-IT")}` : ` · ${giorniFatturati} gg fatturati`}
                {n.dataFineEffettiva && n.tariffaTipo === "giornaliera" && giorniFatturati !== giorniCalendario ? ` (${giorniFatturati} gg fatturati su ${giorniCalendario} totali)` : ""}
                {n.spostatoInId ? <span style={{ color: "#1d4ed8" }}> · proseguito su altra commessa →</span> : ""}
                {n.spostatoDaId ? <span style={{ color: "#1d4ed8" }}> · ← continua da un altro cantiere</span> : ""}
              </div>
              {trasp && <div style={{ fontSize: 10.5, color: "#9a3412", marginTop: 2 }}>🚚 {trasp}</div>}
              {(n.rettifiche || []).length > 0 && (() => {
                const ultima = n.rettifiche[n.rettifiche.length - 1];
                return (
                  <div title={n.rettifiche.map(r => `${new Date(r.il).toLocaleDateString("it-IT")}: ${testoRettifica(r)}`).join("\n")}
                    style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 4, fontSize: 11, color: n.rettificaDaVedere ? "#92400e" : "#6b7280", background: n.rettificaDaVedere ? "#fffbeb" : "#f9fafb", border: `1px solid ${n.rettificaDaVedere ? "#fde68a" : "#f3f4f6"}`, borderRadius: 6, padding: "3px 8px" }}>
                    <b>✎ Rettificato{n.rettifiche.length > 1 ? ` (${n.rettifiche.length} volte)` : ""}</b>
                    <span>{new Date(ultima.il).toLocaleDateString("it-IT")}: {testoRettifica(ultima)}</span>
                    {n.rettificaDaVedere && vedeRettifiche && (
                      <button onClick={() => segnaRettificheViste(n)} style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 700, color: "#92400e", background: "#fff", border: "1px solid #fde68a", borderRadius: 5, padding: "1px 7px", cursor: "pointer" }}>Visto</button>
                    )}
                  </div>
                );
              })()}
              {n.tariffaTipo === "giornaliera" && (
                <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 2 }}>
                  € {formatValutaItaliana(numIt(n.importoGiornaliero) || 0)}/giorno
                  {!n.dataFineEffettiva && giorniFatturati !== giorniCalendario ? ` · ${giorniFatturati} gg fatturati (di ${giorniCalendario} trascorsi)` : ""}
                  {haEsclusioni && <span style={{ color: "#7c3aed" }}> · {[!n.escludiWeekend ? null : "no weekend", !n.escludiFestivi ? null : "no festivi", n.sospensioni?.length ? `${n.sospensioni.length} sospensione/i` : null].filter(Boolean).join(" · ")}</span>}
                  {n.costoPrevisto != null && <> · previsto € {formatValutaItaliana(n.costoPrevisto)}
                    {residuo !== 0 && <span style={{ color: residuo < 0 ? "#dc2626" : "#059669", fontWeight: 700 }}> · residuo € {formatValutaItaliana(residuo)}</span>}
                  </>}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: attivo ? "#d97706" : "#059669", whiteSpace: "nowrap" }}>€ {formatValutaItaliana(costo)}</div>
              <div style={{ fontSize: 9, color: "#9ca3af" }}>{attivo ? "costo ad oggi" : "costo finale"}</div>
            </div>
            {!attivo && (
              <div style={{ display: "flex", gap: 1 }} title={n.votoAffidabilita ? `Affidabilità votata: ${n.votoAffidabilita}/5` : "Vota l'affidabilità di questo fornitore"}>
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={13}
                    onClick={canChiudere ? () => vota(n, s) : undefined}
                    style={{ cursor: canChiudere ? "pointer" : "default", color: (n.votoAffidabilita || 0) >= s ? "#f59e0b" : "#e5e7eb" }}
                    fill={(n.votoAffidabilita || 0) >= s ? "#f59e0b" : "none"} />
                ))}
              </div>
            )}
            {canManage && (
              <button onClick={() => apriModifica(n)}
                style={{ background: "#f9fafb", border: "1px solid #e5e7eb", color: "#374151", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Modifica
              </button>
            )}
            {canChiudere && n.tariffaTipo === "giornaliera" && (
              <button onClick={() => { setSospensioniTarget(n); setNuovaSospensione({ dal: "", al: "", motivo: "" }); }}
                style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#6d28d9", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Sospensioni{n.sospensioni?.length ? ` (${n.sospensioni.length})` : ""}
              </button>
            )}
            {attivo && n.categoria === "mezzo_opera" && canChiudere && n.tariffaTipo === "giornaliera" && (
              <button onClick={() => { setSpostaTarget(n); setSpostaForm({ nuovoProjectId: "", dataPassaggio: oggi() }); }}
                style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Sposta
              </button>
            )}
            {attivo && n.categoria === "mezzo_opera" && canChiudere && n.tariffaTipo === "forfettario" && (
              <span title="Lo spostamento automatico non è disponibile per i noleggi a importo forfettario: l'importo totale non può essere diviso in automatico tra due commesse in modo corretto. Per spostare questo mezzo chiudi il noleggio e creane uno nuovo sulla nuova commessa, decidendo tu come dividere l'importo."
                style={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic", whiteSpace: "nowrap" }}>
                Sposta non disp.
              </span>
            )}
            {attivo && canChiudere && (
              <button onClick={() => apriChiusura(n)}
                style={{ background: "#fff7ed", border: "1px solid #fed7aa", color: "#c2410c", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Chiudi
              </button>
            )}
            {!attivo && canChiudere && (
              <button onClick={() => apriChiusura(n, true)} title="Correggi la data di fine (rettifica: serve un motivo, avvisa titolare e admin)"
                style={{ background: "#fff", border: "1px solid #e5e7eb", color: "#6b7280", padding: "5px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                Correggi fine
              </button>
            )}
            {attivo && !canChiudere && <span title="Solo titolare e amministrazione possono chiudere un noleggio" style={{ color: "#d1d5db" }}><Lock size={13} /></span>}
            {canChiudere && (
              <button onClick={() => elimina(n)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 4 }}><Trash2 size={14} /></button>
            )}
          </div>
        );
      }))}

      {scheda === "fornitori" && <ResocontoNoleggiFornitori noleggi={noleggi} projects={projects} orgInfo={orgInfo} />}

      {scheda === "confronto" && (
        <div>
          <p style={{ fontSize: 11.5, color: "#9ca3af", marginBottom: 14 }}>Solo mezzi d'opera a tariffa giornaliera, raggruppati per semestre — così il confronto è sempre tra prezzi dello stesso periodo, mai falsato dall'inflazione nel tempo.</p>
          {confrontoPrezzi.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af", fontSize: 12.5 }}>Nessun dato ancora comparabile.</div>
          ) : confrontoPrezzi.map(g => {
            const t = TIPI_MEZZO_OPERA[g.tipo] || { label: g.tipo, emoji: "📦" };
            return (
              <div key={g.tipo + g.semestre} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#374151", marginBottom: 6 }}>{t.emoji} {t.label} — {g.semestre}</div>
                {g.fornitori.map((f, i) => (
                  <div key={f.fornitore} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: i === 0 ? "#f0fdf4" : "#fff", border: `1px solid ${i === 0 ? "#bbf7d0" : "#f3f4f6"}`, borderRadius: 8, marginBottom: 4 }}>
                    {i === 0 && <span style={{ fontSize: 10, fontWeight: 700, color: "#059669" }}>PIÙ CONVENIENTE</span>}
                    <span style={{ flex: 1, fontSize: 12, color: "#374151" }}>{f.fornitore} <span style={{ color: "#9ca3af" }}>({f.count} noleggi)</span></span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>€ {formatValutaItaliana(f.prezzoMedio)}/giorno</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {chiudiTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setChiudiTarget(null)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: "#c2410c", margin: 0 }}>{correzione ? "Correggi la fine del noleggio" : "Chiudi noleggio"}</h3>
              <button className="btn-icon" onClick={() => setChiudiTarget(null)}><X size={15} /></button>
            </div>
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 14 }}>
              {correzione ? "Il noleggio è già chiuso: correggere la data cambia un costo già registrato." : "Inserisci la data reale di fine noleggio. Il costo nel Controllo Economico si ferma su questa data."}
            </p>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Data fine effettiva *</label>
            <input type="date" value={dataChiusuraForm} max={oggi()} min={chiudiTarget.dataInizio} onChange={e => setDataChiusuraForm(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, marginBottom: 12, boxSizing: "border-box" }} />
            {!correzione && !chiudiTarget.trasportoCompreso && (
              <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "8px 10px", marginBottom: 12 }}>
                <label htmlFor="ritiro-chiusura" style={{ fontSize: 11, fontWeight: 700, color: "#9a3412", display: "block", marginBottom: 4 }}>🚚 Trasporto di ritiro € *</label>
                <input id="ritiro-chiusura" value={ritiroChiusura} onChange={e => setRitiroChiusura(e.target.value)} placeholder="0 se il noleggiatore non lo fa pagare" inputMode="decimal"
                  style={{ width: "100%", padding: "7px 9px", border: "1px solid #fed7aa", borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
                <div style={{ fontSize: 10.5, color: "#9a3412", marginTop: 3 }}>{chiudiTarget.trasportoRitiro != null ? "Proposto quello indicato all'apertura: correggilo se il noleggiatore ha chiesto un importo diverso." : "Il costo del ritiro che il noleggiatore addebita: entra nel costo del noleggio."}</div>
              </div>
            )}
            {dataChiusuraForm && eRettifica(dataChiusuraForm, correzione) && (() => {
              const prima = costoNoleggio(chiudiTarget);
              const dopo = costoNoleggio({ ...chiudiTarget, dataFineEffettiva: dataChiusuraForm });
              const diff = dopo - prima;
              return (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#92400e", marginBottom: 4 }}>✎ Questa è una rettifica</div>
                  <div style={{ fontSize: 11.5, color: "#78350f", lineHeight: 1.5, marginBottom: 8 }}>
                    {correzione ? "Si cambia la data di un noleggio già chiuso" : "La data è più vecchia di ieri"}: il costo passa da <b>€ {formatValutaItaliana(prima)}</b> a <b>€ {formatValutaItaliana(dopo)}</b> ({diff < 0 ? "−" : "+"}€ {formatValutaItaliana(Math.abs(diff))}).
                    Resta registrata per sempre e avvisa titolare e admin; compare in Cabina di Regia e in Nexiora Insight.
                  </div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#78350f", display: "block", marginBottom: 3 }}>Motivo *</label>
                  <input value={motivoRettifica} onChange={e => setMotivoRettifica(e.target.value)} placeholder="Es. Piattaforma ritirata il 03/10, chiusura dimenticata"
                    style={{ width: "100%", padding: "7px 9px", border: "1px solid #fde68a", borderRadius: 6, fontSize: 12.5, marginBottom: 8, boxSizing: "border-box" }} />
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#78350f", display: "block", marginBottom: 3 }}>Riferimento del fornitore <span style={{ fontWeight: 400 }}>(consigliato: bolla di ritiro, n. documento)</span></label>
                  <input value={riferimentoRettifica} onChange={e => setRiferimentoRettifica(e.target.value)} placeholder="Es. Bolla di ritiro n. 1234 del 03/10"
                    style={{ width: "100%", padding: "7px 9px", border: "1px solid #fde68a", borderRadius: 6, fontSize: 12.5, boxSizing: "border-box" }} />
                </div>
              );
            })()}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={() => setChiudiTarget(null)}>Annulla</button>
              <button onClick={confermaChiusura} disabled={savingChiusura}
                style={{ padding: "8px 20px", border: "none", borderRadius: 8, background: "#c2410c", color: "#fff", fontSize: 13, fontWeight: 700, cursor: savingChiusura ? "not-allowed" : "pointer" }}>
                {savingChiusura ? "..." : eRettifica(dataChiusuraForm, correzione) ? "Conferma rettifica" : "Conferma chiusura"}
              </button>
            </div>
          </div>
        </div>
      )}

      {spostaTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSpostaTarget(null)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: "#1d4ed8", margin: 0 }}>Sposta noleggio su altra commessa</h3>
              <button className="btn-icon" onClick={() => setSpostaTarget(null)}><X size={15} /></button>
            </div>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 11.5, color: "#1e40af", lineHeight: 1.5 }}>
              💡 <strong>Cosa fa "Sposta":</strong> usalo quando questo mezzo d'opera smette di lavorare su questa commessa e passa a un'altra, senza mai tornare indietro dal fornitore. Il noleggio attuale si chiude in automatico il giorno prima del passaggio (il suo costo resta bloccato su questa commessa), e ne parte uno identico sulla nuova commessa dal giorno del passaggio — nessun giorno di noleggio contato due volte, nessuna commessa che paga il costo dell'altra.
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10 }}>
              {TIPI_MEZZO_OPERA[spostaTarget.tipo]?.label} · {spostaTarget.fornitore} — attualmente su {spostaTarget.projectNome || "—"}
            </div>
            {!spostaTarget.trasportoCompreso && (
              <div style={{ fontSize: 11.5, color: "#9a3412", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "6px 10px", marginBottom: 10 }}>
                🚚 La consegna resta su questa commessa; il ritiro passa alla nuova, da cui il mezzo tornerà dal noleggiatore.
              </div>
            )}
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Nuova commessa *</label>
            <select value={spostaForm.nuovoProjectId} onChange={e => setSpostaForm(f => ({ ...f, nuovoProjectId: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, marginBottom: 10, boxSizing: "border-box" }}>
              <option value="">— Seleziona commessa —</option>
              {projectsAperti.filter(p => p.id !== spostaTarget.projectId).map(p => <option key={p.id} value={p.id}>{p.codiceCommessa ? `[${p.codiceCommessa}] ` : ""}{p.name}</option>)}
            </select>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Data di passaggio *</label>
            <input type="date" value={spostaForm.dataPassaggio} min={spostaTarget.dataInizio} onChange={e => setSpostaForm(f => ({ ...f, dataPassaggio: e.target.value }))}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, marginBottom: 16, boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={() => setSpostaTarget(null)}>Annulla</button>
              <button onClick={confermaSposta} disabled={savingSposta}
                style={{ padding: "8px 20px", border: "none", borderRadius: 8, background: "#1d4ed8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: savingSposta ? "not-allowed" : "pointer" }}>
                {savingSposta ? "..." : "Conferma spostamento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modificaTarget && modificaForm && (() => {
        const TIPI_M = modificaTarget.categoria === "mezzo_opera" ? TIPI_MEZZO_OPERA : TIPI_VEICOLO;
        const ant = anteprimaCosto(modificaForm);
        return (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModificaTarget(null)}>
            <div className="modal" style={{ maxWidth: 420 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <h3 style={{ fontSize: 15, fontWeight: 800, color: "#374151", margin: 0 }}>Modifica noleggio</h3>
                <button className="btn-icon" onClick={() => setModificaTarget(null)}><X size={15} /></button>
              </div>
              <div style={{ fontSize: 11.5, color: "#6b7280", marginBottom: 12 }}>
                {modificaTarget.projectNome || "Generico aziendale"} — per cambiare commessa usa "Sposta", per chiudere usa "Chiudi".
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, padding: "8px 12px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <span style={{ fontSize: 16 }}>{(TIPI_M[modificaForm.tipo] || {}).emoji}</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#374151" }}>{(TIPI_M[modificaForm.tipo] || {}).label}</span>
                <span title="Il tipo di mezzo non è modificabile: per cambiarlo elimina questo noleggio e registrane uno nuovo." style={{ marginLeft: "auto" }}><Lock size={12} color="#9ca3af" /></span>
              </div>

              <SceltaNoleggiatore valore={modificaForm.fornitoreId} nomeAttuale={modificaForm.fornitore} onScegli={({ id, denominazione }) => setModificaForm(f => ({ ...f, fornitoreId: id, fornitore: denominazione }))} fornitori={fornitori} puoAggiungere={puoAggiungereFornitori} orgId={effectiveOrgId} style={{ marginBottom: 8 }} />
              <input value={modificaForm.identificativo} onChange={e => setModificaForm(f => ({ ...f, identificativo: e.target.value }))} placeholder="Identificativo/targa (facoltativo)"
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 8, boxSizing: "border-box" }} />

              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Data inizio *</label>
                  <input type="date" value={modificaForm.dataInizio} onChange={e => setModificaForm(f => ({ ...f, dataInizio: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Data fine prevista{modificaTarget?.categoria === "mezzo_opera" ? " *" : ""} <span style={{ fontWeight: 400, color: "#9ca3af" }}>(per prorogare, spostala in avanti)</span></label>
                  <input type="date" value={modificaForm.dataFinePrevista} onChange={e => setModificaForm(f => ({ ...f, dataFinePrevista: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box" }} />
                </div>
              </div>

              <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", cursor: "pointer" }}>
                  <input type="checkbox" checked={modificaForm.escludiWeekend} onChange={e => setModificaForm(f => ({ ...f, escludiWeekend: e.target.checked }))} />
                  Escludi sabato e domenica
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151", cursor: "pointer" }}>
                  <input type="checkbox" checked={modificaForm.escludiFestivi} onChange={e => setModificaForm(f => ({ ...f, escludiFestivi: e.target.checked }))} />
                  Escludi festivi italiani
                </label>
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                {[{ id: "giornaliera", label: "Tariffa giornaliera" }, { id: "forfettario", label: "Importo forfettario" }].map(t => (
                  <button key={t.id} type="button" onClick={() => setModificaForm(f => ({ ...f, tariffaTipo: t.id }))}
                    style={{ flex: 1, padding: "8px", borderRadius: 8, border: `1px solid ${modificaForm.tariffaTipo === t.id ? "#7c3aed" : "#e5e7eb"}`, background: modificaForm.tariffaTipo === t.id ? "#f5f3ff" : "#fff", color: modificaForm.tariffaTipo === t.id ? "#7c3aed" : "#6b7280", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    {t.label}
                  </button>
                ))}
              </div>
              {modificaForm.tariffaTipo === "giornaliera" ? (
                <input value={modificaForm.importoGiornaliero} onChange={e => setModificaForm(f => ({ ...f, importoGiornaliero: e.target.value }))} placeholder="€ / giorno *" inputMode="decimal"
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 10, boxSizing: "border-box" }} />
              ) : (
                <input value={modificaForm.importoForfettario} onChange={e => setModificaForm(f => ({ ...f, importoForfettario: e.target.value }))} placeholder="Importo totale € *" inputMode="decimal"
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, marginBottom: 10, boxSizing: "border-box" }} />
              )}

              <CampiTrasporto f={modificaForm} set={x => setModificaForm(f => ({ ...f, ...x }))} bloccato={!!modificaTarget.dataFineEffettiva && !canChiudere}
                nota={modificaTarget.dataFineEffettiva ? (canChiudere ? "Noleggio chiuso: cambiando i trasporti cambia un costo già registrato in commessa." : "Noleggio chiuso: i trasporti li corregge titolare o amministrazione.") : ""} />

              {ant && (
                <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "8px 14px", marginBottom: 16, fontSize: 12, color: "#5b21b6" }}>
                  💰 {ant.giorni != null
                    ? <>{ant.giorni} giorni × € {formatValutaItaliana(numIt(modificaForm.importoGiornaliero))}/giorno{ant.trasporti ? <> + trasporti € {formatValutaItaliana(ant.trasporti)}</> : null} = <strong>€ {formatValutaItaliana(ant.costo)}</strong>{ant.aperta ? " (fino ad oggi, ancora aperto)" : " (fino alla data fine prevista)"}</>
                    : <>Costo forfettario{ant.trasporti ? <> € {formatValutaItaliana(ant.nolo)} + trasporti € {formatValutaItaliana(ant.trasporti)}</> : null}: <strong>€ {formatValutaItaliana(ant.costo)}</strong></>}
                </div>
              )}

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn btn-secondary" onClick={() => setModificaTarget(null)}>Annulla</button>
                <button onClick={salvaModifica} disabled={savingModifica}
                  style={{ padding: "8px 20px", border: "none", borderRadius: 8, background: "#374151", color: "#fff", fontSize: 13, fontWeight: 700, cursor: savingModifica ? "not-allowed" : "pointer" }}>
                  {savingModifica ? "..." : "Salva modifiche"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {sospensioniTarget && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSospensioniTarget(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: "#6d28d9", margin: 0 }}>Sospensioni costo</h3>
              <button className="btn-icon" onClick={() => setSospensioniTarget(null)}><X size={15} /></button>
            </div>
            <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 11.5, color: "#5b21b6", lineHeight: 1.5 }}>
              💡 Da usare solo se il fornitore ha accettato di <strong>sospendere il costo</strong> per un periodo (es. ferie estive o natalizie) mentre il mezzo resta comunque in cantiere — va prima concordato con il fornitore da titolare o amministrazione, non è un'esclusione automatica. Nei giorni sospesi il costo non matura, anche se il noleggio risulta ancora attivo.
            </div>

            {(sospensioniTarget.sospensioni || []).length > 0 && (
              <div style={{ marginBottom: 14 }}>
                {sospensioniTarget.sospensioni.map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#fff", border: "1px solid #f3f4f6", borderRadius: 7, marginBottom: 4 }}>
                    <span style={{ flex: 1, fontSize: 12, color: "#374151" }}>
                      {new Date(s.dal + "T00:00:00").toLocaleDateString("it-IT")} → {new Date(s.al + "T00:00:00").toLocaleDateString("it-IT")}
                      {s.motivo ? <span style={{ color: "#9ca3af" }}> · {s.motivo}</span> : ""}
                    </span>
                    <button onClick={() => eliminaSospensione(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 2 }}><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Dal *</label>
                <input type="date" value={nuovaSospensione.dal} onChange={e => setNuovaSospensione(s => ({ ...s, dal: e.target.value }))}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12.5, boxSizing: "border-box" }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Al *</label>
                <input type="date" value={nuovaSospensione.al} onChange={e => setNuovaSospensione(s => ({ ...s, al: e.target.value }))}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12.5, boxSizing: "border-box" }} />
              </div>
            </div>
            <input value={nuovaSospensione.motivo} onChange={e => setNuovaSospensione(s => ({ ...s, motivo: e.target.value }))} placeholder="Motivo (es. ferie estive) — facoltativo"
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 12.5, marginBottom: 16, boxSizing: "border-box" }} />

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary" onClick={() => setSospensioniTarget(null)}>Chiudi</button>
              <button onClick={aggiungiSospensione} disabled={savingSospensione}
                style={{ padding: "8px 20px", border: "none", borderRadius: 8, background: "#6d28d9", color: "#fff", fontSize: 13, fontWeight: 700, cursor: savingSospensione ? "not-allowed" : "pointer" }}>
                {savingSospensione ? "..." : "Aggiungi sospensione"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
